/**
 * Grand Quack Auto — NPC ragdoll launch helpers (Phase B2).
 *
 * Mega-quack sends every NPC inside a radius flying. Implementation is
 * integrated arithmetic, not a cannon-es body — NPCs aren't physics objects
 * (would invite collision-filter complexity for 25 of them) and we just
 * need the visual gag, not interactive collision.
 *
 * Lifecycle: triggerRagdollLaunch() flips state and seeds velocities;
 * updateRagdollNpc() integrates one tick and returns true when the NPC
 * has served its time. The caller (npcSystem) handles snap-to-waypoint
 * and state restore so this module stays free of waypoint plumbing.
 *
 * Extracted from npcSystem so that file stays under the 200-line cap.
 */

import * as THREE from 'three';
import { NPC } from '../config';
import type { PedestrianEntity } from '../entities/pedestrian';
import type { World } from '../types';

// Reusable scratch vector — avoids per-launch allocation.
const _away = new THREE.Vector3();

/**
 * Send every non-ragdolling NPC inside `radius` flying with a radial XZ
 * blast + upward arc + random tumble. Returns the launched NPCs so the
 * caller can fold them into chaos / popups.
 */
export function triggerRagdollLaunch(
  world: World,
  origin: THREE.Vector3,
  radius: number,
  npcs: ReadonlyArray<PedestrianEntity>,
): PedestrianEntity[] {
  const launched: PedestrianEntity[] = [];
  for (const npc of npcs) {
    if (npc.state === 'ragdoll') continue;
    if (npc.group.position.distanceTo(origin) > radius) continue;

    _away.copy(npc.group.position).sub(origin);
    _away.y = 0;
    if (_away.lengthSq() < 0.0001) {
      // Zero-distance edge case — pick a random horizontal direction.
      _away.set(Math.random() - 0.5, 0, Math.random() - 0.5);
    }
    _away.normalize();

    npc.ragdollHorizVel.copy(_away).multiplyScalar(NPC.ragdollLaunchSpeed);
    npc.ragdollVelY = NPC.ragdollLaunchUp;
    npc.ragdollAngVel.set(
      (Math.random() * 2 - 1) * NPC.ragdollMaxAngVel,
      (Math.random() * 2 - 1) * NPC.ragdollMaxAngVel,
      (Math.random() * 2 - 1) * NPC.ragdollMaxAngVel,
    );
    npc.ragdollBounced = false;
    npc.stateTimer = NPC.ragdollDurationSeconds;
    npc.state = 'ragdoll';
    launched.push(npc);
  }
  void world; // reserved for future audio / popup callbacks
  return launched;
}

/**
 * Integrate one ragdoll NPC for `dt`. Returns true when the NPC has
 * finished its airtime — caller should snap it to a waypoint and flip
 * back to 'walking'.
 */
export function updateRagdollNpc(npc: PedestrianEntity, dt: number): boolean {
  const pos = npc.group.position;
  // Horizontal velocity is constant (no air drag); vertical falls under gravity.
  pos.x += npc.ragdollHorizVel.x * dt;
  pos.z += npc.ragdollHorizVel.z * dt;
  npc.ragdollVelY += NPC.ragdollGravity * dt;
  pos.y += npc.ragdollVelY * dt;

  // Tumble — the visual gold. Per-axis euler rotation on the group.
  npc.group.rotation.x += npc.ragdollAngVel.x * dt;
  npc.group.rotation.y += npc.ragdollAngVel.y * dt;
  npc.group.rotation.z += npc.ragdollAngVel.z * dt;

  // One ground bounce, then settle (no infinite jiggle).
  if (pos.y <= 0) {
    pos.y = 0;
    if (!npc.ragdollBounced && npc.ragdollVelY < -1) {
      npc.ragdollVelY = -npc.ragdollVelY * NPC.ragdollBounceRestitution;
      npc.ragdollHorizVel.multiplyScalar(NPC.ragdollBounceRestitution);
      npc.ragdollAngVel.multiplyScalar(NPC.ragdollBounceRestitution);
      npc.ragdollBounced = true;
    } else {
      npc.ragdollVelY = 0;
      npc.ragdollHorizVel.set(0, 0, 0);
    }
  }

  npc.stateTimer -= dt;
  return npc.stateTimer <= 0;
}

/** Reset ragdoll state on an NPC — caller does the state/waypoint flip. */
export function clearRagdollState(npc: PedestrianEntity): void {
  npc.group.position.y = 0;
  npc.group.rotation.set(0, npc.group.rotation.y, 0);
  npc.ragdollHorizVel.set(0, 0, 0);
  npc.ragdollAngVel.set(0, 0, 0);
  npc.ragdollVelY = 0;
  npc.ragdollBounced = false;
}
